import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AuthService,
  ErrorKeys,
  ErrorMessagesComponent,
  ErrorStateHighlightDirective,
  GbTheme,
  GoogleTagService,
  MarkFormTouchedDirective,
  PasswordInputComponent,
  SocialLoginButtonsComponent,
  THEME_SIGNAL,
} from '@gb-webapp/gb-app';
import { TranslateModule } from '@ngx-translate/core';
import { finalize, map, Observable, of, tap } from 'rxjs';

const INVALID_CREDENTIALS_MESSAGE = 'authentication.invalid_credentials';

@Component({
  selector: 'app-sign-in',
  imports: [
    ReactiveFormsModule,
    SocialLoginButtonsComponent,
    TranslateModule,
    MarkFormTouchedDirective,
    ErrorMessagesComponent,
    ErrorStateHighlightDirective,
    PasswordInputComponent,
  ],
  templateUrl: './sign-in.component.html',
  styleUrl: './sign-in.component.scss',
})
export class SignInComponent {
  signInForm = new FormGroup({
    email: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.email,
        Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
      ],
    }),

    password: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  protected theme = inject(THEME_SIGNAL);

  public GbTheme = GbTheme;

  protected googleTagService = inject(GoogleTagService);

  protected router = inject(Router);

  public autoRedirectOnSocialLogin = input<boolean>(false);

  public postAuthRedirect = input<{ url?: string; queryParams?: { [key: string]: string } }>({});

  public goToForgotPassword = output();

  isSubmitting = signal(false);

  protected authService = inject(AuthService);

  shouldShowValidationErrors = false;

  errorMessage = signal<false | string>(false);

  public serverErrors = signal({
    email: '',
    password: '',
  });

  public signedIn = output();

  // Centralized validation rules object
  fieldValidationRules = computed(() => {
    const serverErrors = this.serverErrors();
    return {
      email: {
        required: 'authentication.email_required',
        email: 'authentication.pattern_error',
        pattern: 'authentication.pattern_error',
        [ErrorKeys.ServerValidation]: serverErrors.email,
      },
      password: {
        required: 'authentication.password_required',
        [ErrorKeys.ServerValidation]: serverErrors.password,
      },
    };
  });

  public goToSignUp = output<void>();

  /**
   * Handles sign-in form submission with validation and API call.
   */
  signIn(): Observable<boolean> {
    if (this.isSubmitting()) {
      return of(false);
    }

    this.isSubmitting.set(true);
    return this.authService
      .signIn(this.signInForm.getRawValue().email, this.signInForm.getRawValue().password)
      .pipe(
        finalize(() => this.isSubmitting.set(false)),
        tap({
          error: (err) => this.mapValidationErrors(err),
        }),
        map(() => {
          this.signedIn.emit();
          this.googleTagService.pushEvent('sign_in', {
            email: this.signInForm.getRawValue().email,
            pageUrl: this.router.url,
            userStatus: 'logged_in',
          });
          return true;
        })
      );
  }

  public mapValidationErrors(error: unknown): void {
    if (!(error instanceof HttpErrorResponse)) {
      return;
    }

    if (error.error.message === INVALID_CREDENTIALS_MESSAGE) {
      this.errorMessage.set('authentication.invalid_credentials');
      return;
    }

    this.errorMessage.set(error.error.message);
  }
}
